import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import {
  EventUsersSchema,
  type EventUsers,
} from "@shared/schemas/event-users.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createEventUsersApi } from "./event-users.api";

export const EVENT_USERS_QUERY_KEYS = {
  byEvent: (eventIdPublic: EventIdPublic) =>
    ["event-users", "event", eventIdPublic] as const,
} as const;

// Changes only when a participant joins or is removed, or a user changes
// WhatsApp number; sibling polling must not refetch it.
const EVENT_USERS_STALE_TIME_MS = 5 * 60_000;

interface GetEventUsersOptions {
  readonly enabled?: boolean;
}

export class EventUsersHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createEventUsersApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetEventUsers: (
        eventIdPublic: EventIdPublic,
        options: GetEventUsersOptions = {},
      ): UseQueryResult<EventUsers> => {
        const { enabled = true } = options;

        return this.query({
          key: EVENT_USERS_QUERY_KEYS.byEvent(eventIdPublic),
          fn: () => api.getEventUsers(eventIdPublic),
          parse: (dto) => EventUsersSchema.parse(dto),
          enabled: enabled && EventIdPublic.unwrap(eventIdPublic).length > 0,
          staleTime: EVENT_USERS_STALE_TIME_MS,
        });
      },

      fetchEventUsers: async (
        eventIdPublic: EventIdPublic,
      ): Promise<EventUsers> =>
        EventUsersSchema.parse(await api.getEventUsers(eventIdPublic)),
    };
  }
}
